import * as React from 'react';
import {Box, Card, CardContent, Skeleton, Typography} from '@mui/material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import {useTranslation} from 'react-i18next';

interface ContractStats {
    activeCount: number;
    endingSoonCount: number;
    newStartsCount: number;
}

interface ContractStatCardsProps {
    readonly stats: ContractStats;
    readonly loading?: boolean;
}

interface StatCardProps {
    readonly icon: React.ReactElement;
    readonly iconBg: string;
    readonly iconColor: string;
    readonly label: string;
    readonly value: number;
    readonly loading?: boolean;
}

function StatCard({icon, iconBg, iconColor, label, value, loading}: Readonly<StatCardProps>): React.ReactElement {
    return (
        <Card variant="outlined" sx={{borderRadius: 1, height: '100%'}}>
            <CardContent sx={{
                display: 'flex',
                alignItems: 'center',
                gap: {xs: 1, sm: 1.5},
                p: {xs: 1.25, sm: 2},
                '&:last-child': {pb: {xs: 1.25, sm: 2}},
            }}>
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: {xs: 36, sm: 44},
                    height: {xs: 36, sm: 44},
                    borderRadius: '50%',
                    backgroundColor: iconBg,
                    color: iconColor,
                    flexShrink: 0,
                }}>
                    {icon}
                </Box>
                <Box sx={{textAlign: 'left', minWidth: 0}}>
                    {loading ? (
                        <Skeleton variant="text" width={48} height={32}/>
                    ) : (
                        <Typography variant="h6" sx={{fontWeight: 700}} noWrap>
                            {value}
                        </Typography>
                    )}
                    <Typography variant="body2" sx={{color: 'text.secondary'}} noWrap>
                        {label}
                    </Typography>
                </Box>
            </CardContent>
        </Card>
    );
}

export default function ContractStatCards({stats, loading}: Readonly<ContractStatCardsProps>): React.ReactElement {
    const {t} = useTranslation();

    return (
        <Box sx={{
            display: 'grid',
            gridTemplateColumns: {xs: 'repeat(3, 1fr)', sm: 'repeat(3, 1fr)'},
            gap: 2,
        }}>
            <StatCard
                icon={<DescriptionOutlinedIcon/>}
                iconBg="success.light"
                iconColor="success.dark"
                label={t('contracts.stats.active')}
                value={stats.activeCount}
                loading={loading}
            />
            <StatCard
                icon={<AccessTimeOutlinedIcon/>}
                iconBg="warning.light"
                iconColor="warning.dark"
                label={t('contracts.stats.endingSoon')}
                value={stats.endingSoonCount}
                loading={loading}
            />
            <StatCard
                icon={<PersonAddAltOutlinedIcon/>}
                iconBg="success.light"
                iconColor="success.dark"
                label={t('contracts.stats.newStarts')}
                value={stats.newStartsCount}
                loading={loading}
            />
        </Box>
    );
}
